// ABOUTME: React Query mutation hooks for creating, updating, and deleting overbooking allowance configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  OverbookingAllowanceApiResponse,
  OverbookingAllowanceFormValues,
  PutOverbookingAllowanceRequest,
} from '../types/overbookingAllowance'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

export const toApiRequest = (values: OverbookingAllowanceFormValues): PutOverbookingAllowanceRequest => ({
  entries: {
    isOverridden: true,
    value: values.entries.map((entry) => ({
      customerTier: entry.customerTier,
      overbookingPercent: entry.overbookingPercent,
      leadTime: {
        value: entry.leadTimeValue,
        uomId: entry.leadTimeUomId,
        referenceEvent: entry.leadTimeReferenceEvent,
      },
    })),
  },
})

export const useCreateOverbookingAllowance = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: OverbookingAllowanceFormValues }) => {
      const res = await api.post<OverbookingAllowanceApiResponse | undefined>(
        `/tuc-configs/overbooking-allowances/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: (data, { scopeId }) => {
      queryClient.setQueriesData<OverbookingAllowanceApiResponse[]>(
        { queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] },
        (old) => {
          if (!old || !data) return old
          const idx = old.findIndex((r) => r.scopeId === scopeId)
          return idx === -1 ? [...old, data] : old.map((r) => (r.scopeId === scopeId ? data : r))
        }
      )
      queryClient.invalidateQueries({ queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({
        queryKey: ['navigatorScopes', 'overbookingAllowance', countryId, businessLine]
      })
    },
  })
}

export const useCreateOverbookingAllowanceWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: OverbookingAllowanceFormValues }) => {
      const res = await api.post<OverbookingAllowanceApiResponse | undefined>(
        '/tuc-configs/overbooking-allowances',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({
        queryKey: ['navigatorScopes', 'overbookingAllowance', countryId, businessLine]
      })
    },
  })
}

export const useUpdateOverbookingAllowance = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: OverbookingAllowanceFormValues }) => {
      const res = await api.put<OverbookingAllowanceApiResponse | undefined>(
        `/tuc-configs/overbooking-allowances/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: (data, { scopeId }) => {
      queryClient.setQueriesData<OverbookingAllowanceApiResponse[]>(
        { queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] },
        (old) => {
          if (!old || !data) return old
          const idx = old.findIndex((r) => r.scopeId === scopeId)
          return idx === -1 ? old : old.map((r) => (r.scopeId === scopeId ? data : r))
        }
      )
      queryClient.invalidateQueries({ queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({
        queryKey: ['navigatorScopes', 'overbookingAllowance', countryId, businessLine]
      })
    },
  })
}

export const useDeleteOverbookingAllowance = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/overbooking-allowances/${scopeId}`)
    },
    onSuccess: (_data, scopeId) => {
      queryClient.setQueriesData<OverbookingAllowanceApiResponse[]>(
        { queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] },
        (old) => old ? old.filter((r) => r.scopeId !== scopeId) : old
      )
      queryClient.invalidateQueries({ queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({
        queryKey: ['navigatorScopes', 'overbookingAllowance', countryId, businessLine]
      })
    },
  })
}
